//index.ts
import { LangStrMap } from '@/constants/locales'
import { computed, watch } from 'vue'
//引入vue-i18n组件
import { createI18n } from 'vue-i18n'
// 英文语言配置
import enUS from './en-US'
// 中文（HK）语言配置
import zhHK from './zh-HK'
// 中文（简体）语言配置
import zhCN from './zh-CN'
// 引入子模块的公共多语言
import commonEnUs from '@/common/src/locales/en-US/index'
import commonZhHk from '@/common/src/locales/zh-HK/index'
import commonZhCn from '@/common/src/locales/zh-CN/index'
// 引入子模块的常量
// import { LOCAL_LANG_KEY_PINGTAI } from '@/common/src/constants/system-constant.ts'
import { LOCAL_LANG_KEY_CLINIC } from '@/constants/cache-key'
/**
 * 判断语言当前
 * @param {Object} mList
 */
function getLanguage(): string {
  // const login = storeToRefs(useLogin())
  // console.log('login- == ', login)
  // 瀏覽器語言為中文（簡體/繁體）時默認繁體，其餘默認英文
  const browserLang = (navigator.language || '').toLowerCase()
  const defaultLan: string = browserLang.startsWith('zh') ? 'zh-HK' : 'en-US'
  // var lang = lang.substr(0, 2);//获取浏览器配置语言前两位
  // return login.currentLang.value || defaultLan
  let localLang: any = localStorage.getItem(LOCAL_LANG_KEY_CLINIC)
  if (!LangStrMap.includes(localLang)) {
    localLang = defaultLan
  }
  const language: string = localLang || defaultLan // 已選擇過語言的沿用其選擇
  localStorage.setItem(LOCAL_LANG_KEY_CLINIC, language)
  return language
}
// 合并公共多语言和项目自有多语言
const messages = {
  'zh-HK': { ...commonZhHk, ...zhHK },
  'zh-CN': { ...commonZhCn, ...zhCN },
  'en-US': { ...commonEnUs, ...enUS },
}

// 创建 i18n 实例并赋值给变量
const i18n = createI18n({
  globalInjection: true,
  legacy: false, // 启用 composition API 模式
  locale: getLanguage(),
  messages: messages,
})

// 套票售賣期改為非必填，提示語按 ClinicApi SavePackageTicketInput 文檔口徑
//（saleStartTime null=立即上架、saleEndTime null=不限期），列表按空值組合顯示
//「{date}開售 / 至 {date} / 不限期」。新 key 暫落本地，後續統一移入 common/src/locales/*。
const localSaleDateHints: Record<string, Record<string, string>> = {
  'zh-HK': {
    taopiao_sale_start_null_hint: '留空即立即上架',
    taopiao_sale_end_null_hint: '留空即不限期',
    BuXianQi: '不限期',
    KaiShou: '{date}開售',
  },
  'zh-CN': {
    taopiao_sale_start_null_hint: '留空即立即上架',
    taopiao_sale_end_null_hint: '留空即不限期',
    BuXianQi: '不限期',
    KaiShou: '{date}开售',
  },
  'en-US': {
    taopiao_sale_start_null_hint: 'Leave blank to go on sale immediately',
    taopiao_sale_end_null_hint: 'Leave blank for no end date',
    BuXianQi: 'No end date',
    KaiShou: 'From {date}',
  },
}
Object.entries(localSaleDateHints).forEach(([lang, text]) => {
  i18n.global.mergeLocaleMessage(lang, {
    Tips_Common: {
      taopiao_sale_start_null_hint: text.taopiao_sale_start_null_hint,
      taopiao_sale_end_null_hint: text.taopiao_sale_end_null_hint,
    },
    Table_Common: {
      BuXianQi: text.BuXianQi,
      KaiShou: text.KaiShou,
    },
  })
})

// 優惠券非用戶券(診所券)改用使用開始/結束時間:ClinicApi SaveCouponsInput.useStartTime
// (null=不限制)、useEndTime(null=長期有效),過期四欄僅用戶券有效;列表有效期按空值組合顯示。
// 新 key 暫落本地,後續統一移入 common/src/locales/*。
const localCouponUseDateKeys: Record<string, Record<string, string>> = {
  'zh-HK': {
    Qi_RiQi: '{date}起',
    coupon_use_start_null_hint: '留空即不限制',
    coupon_use_end_null_hint: '留空即長期有效',
  },
  'zh-CN': {
    Qi_RiQi: '{date}起',
    coupon_use_start_null_hint: '留空即不限制',
    coupon_use_end_null_hint: '留空即长期有效',
  },
  'en-US': {
    Qi_RiQi: 'From {date}',
    coupon_use_start_null_hint: 'Leave blank for no start limit',
    coupon_use_end_null_hint: 'Leave blank for long-term validity',
  },
}
Object.entries(localCouponUseDateKeys).forEach(([lang, text]) => {
  i18n.global.mergeLocaleMessage(lang, {
    Tips_Common: {
      coupon_use_start_null_hint: text.coupon_use_start_null_hint,
      coupon_use_end_null_hint: text.coupon_use_end_null_hint,
    },
    Table_Common: {
      Qi_RiQi: text.Qi_RiQi,
    },
  })
})

// 藥品使用成本報告（/statistics/cost）表頭：後端只提供全窗合計，用量與成本列名需區別於單價。
// 新 key 暫落本地，後續統一移入 common/src/locales/*。
const localMedicantUseReportKeys: Record<string, Record<string, string>> = {
  'zh-HK': {
    Zong_YongLiang: '總用量',
    Zong_ChengBen: '總成本',
  },
  'zh-CN': {
    Zong_YongLiang: '总用量',
    Zong_ChengBen: '总成本',
  },
  'en-US': {
    Zong_YongLiang: 'Total usage',
    Zong_ChengBen: 'Total cost',
  },
}
Object.entries(localMedicantUseReportKeys).forEach(([lang, text]) => {
  i18n.global.mergeLocaleMessage(lang, {
    Table_Common: {
      Zong_YongLiang: text.Zong_YongLiang,
      Zong_ChengBen: text.Zong_ChengBen,
    },
  })
})

// 對賬單收費總結彈窗：應付 / 退款 / 實收 結算摘要有關文案。
// 新 key 暫落本地，後續統一移入 common/src/locales/*。
const localSettlementKeys: Record<string, Record<string, string>> = {
  'zh-HK': {
    YingFu: '應付',
    ShiShou: '實收',
    DuoShou: '多收',
    YiShou: '已收',
    ChaE: '差額',
    ChaE_MingXi: '差額明細',
    DuoShou_MingXi: '多收明細',
    JiaZai_ShiBai: '載入失敗',
    ShouFei_FangFa: '收費方法',
  },
  'zh-CN': {
    YingFu: '应付',
    ShiShou: '实收',
    DuoShou: '多收',
    YiShou: '已收',
    ChaE: '差额',
    ChaE_MingXi: '差额明细',
    DuoShou_MingXi: '多收明细',
    JiaZai_ShiBai: '加载失败',
    ShouFei_FangFa: '收费方法',
  },
  'en-US': {
    YingFu: 'Amount Due',
    ShiShou: 'Net Amount',
    DuoShou: 'Overpaid',
    YiShou: 'Collected',
    ChaE: 'Difference',
    ChaE_MingXi: 'Difference details',
    DuoShou_MingXi: 'Overpaid details',
    JiaZai_ShiBai: 'Load failed',
    ShouFei_FangFa: 'Payment Methods',
  },
}
Object.entries(localSettlementKeys).forEach(([lang, text]) => {
  i18n.global.mergeLocaleMessage(lang, {
    Table_Common: {
      YingFu: text.YingFu,
      ShiShou: text.ShiShou,
      DuoShou: text.DuoShou,
      YiShou: text.YiShou,
      ChaE: text.ChaE,
      ChaE_MingXi: text.ChaE_MingXi,
      DuoShou_MingXi: text.DuoShou_MingXi,
      JiaZai_ShiBai: text.JiaZai_ShiBai,
      ShouFei_FangFa: text.ShouFei_FangFa,
    },
  })
})

// 患者管理電話長度上限放寬至 15 位（原 7~11），校驗提示語同步改為 7~15 口徑。
// 新 key 暫落本地，後續統一移入 common/src/locales/*。
const localPatientPhoneLengthKeys: Record<string, string> = {
  'zh-HK': '長度在 7 到 15 個字符',
  'zh-CN': '长度在 7 到 15 个字符',
  'en-US': 'Length must be 7 to 15 characters',
}
Object.entries(localPatientPhoneLengthKeys).forEach(([lang, text]) => {
  i18n.global.mergeLocaleMessage(lang, {
    Tips_Common: {
      ChangDu_7_Zhi_15: text,
    },
  })
})

// 导出 i18n 实例和全局 t 函数（关键：供非组件文件使用）
export const t = i18n.global.t

export const currentLocale = computed(() => i18n.global.locale.value)
export const isEn = computed(() => currentLocale.value === 'en-US')
export const isZhCN = computed(() => currentLocale.value === 'zh-CN')

// 同步 <html lang> 到当前语言（含首次加载）
watch(
  currentLocale,
  (lang) => {
    document.documentElement.setAttribute('lang', lang)
  },
  { immediate: true },
)

export default i18n
